// Cookie-less metrics of tallerza.cl. The site sends one beacon per event to
// `POST /api/evento` (Function `evento`), which aggregates it into
// `metricas/{AAAA-MM-DD}` (America/Santiago day) with counters only: no IP,
// user agent or identifiers. Contract: docs/comercial/lanzamiento.md.
import * as z from 'zod/mini';

export const TIPOS_EVENTO = ['vista', 'whatsapp', 'demo_abierta', 'ingresar', 'lead'] as const;
export type TipoEvento = (typeof TIPOS_EVENTO)[number];

export const ETIQUETAS_EVENTO: Record<TipoEvento, string> = {
  vista: 'Vistas',
  whatsapp: 'WhatsApp',
  demo_abierta: 'Demo abierta',
  ingresar: 'Ingresar',
  lead: 'Leads',
};

/** Body of the beacon, as the site sends it. */
export type Evento = {
  t: TipoEvento;
  /** Site path ("/", "/desabolladura-y-pintura"), no query or fragment. */
  ruta: string;
  /** Host of an external referrer ("www.google.com"). */
  ref?: string | undefined;
  utm_source?: string | undefined;
  utm_medium?: string | undefined;
  utm_campaign?: string | undefined;
  movil: boolean;
};

export const MAPAS_METRICAS = [
  'vistas',
  'eventos',
  'referentes',
  'fuentes',
  'medios',
  'campanas',
  'dispositivos',
] as const;
export type MapaMetricas = (typeof MAPAS_METRICAS)[number];

/**
 * Max distinct keys per map and day. Past it, new keys count under
 * CLAVE_OTROS so a flood of made-up values cannot bloat the document (the
 * cap is checked before the write, so concurrent requests may overshoot it
 * by a few keys).
 */
export const TOPE_CLAVES: Record<MapaMetricas, number> = {
  vistas: 100,
  eventos: TIPOS_EVENTO.length,
  referentes: 50,
  fuentes: 30,
  medios: 30,
  campanas: 30,
  dispositivos: 2,
};
export const CLAVE_OTROS = 'otros';
export const MAX_CLAVE = 60;

/**
 * `metricas/{AAAA-MM-DD}`. Every map is optional (created on first
 * increment). Only 'vista' events feed vistas (by path), referentes,
 * fuentes, medios, campanas and dispositivos; eventos counts every type
 * (so eventos.vista is the day's total views). Keys go through claveMetrica:
 * vistas keys are paths with '/' as '_' ("_" is the home page) and referentes
 * keys are hosts with '.' as '_'; claveLegible reverses both.
 */
export type Metricas = {
  /** Same as the id, for range queries. */
  fecha: string;
} & Partial<Record<MapaMetricas, Record<string, number>>>;

export type Dispositivo = 'movil' | 'escritorio';

/**
 * Map key: lowercased, control characters dropped, whitespace and the
 * characters Firestore field paths treat specially (. / ~ * [ ] `) as '_',
 * at most MAX_CLAVE characters. Reserved names (__x__) and empty values fall
 * back to CLAVE_OTROS.
 */
export function claveMetrica(valor: string): string {
  const clave = valor
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s./~*[\]`]/g, '_')
    .slice(0, MAX_CLAVE);
  return clave === '' || /^__.*__$/.test(clave) ? CLAVE_OTROS : clave;
}

/** Readable form of a key: path for vistas, host for referentes. */
export function claveLegible(mapa: MapaMetricas, clave: string): string {
  if (clave === CLAVE_OTROS) return 'Otros';
  if (mapa === 'vistas') return clave.replaceAll('_', '/');
  if (mapa === 'referentes') return clave.replaceAll('_', '.');
  return clave;
}

/** Normalized site path: lowercase, no query/fragment, no repeated or trailing '/'. */
export function normalizarRuta(ruta: string): string | null {
  const limpia = (ruta.trim().split(/[?#]/)[0] ?? '')
    .toLowerCase()
    .replace(/\/{2,}/g, '/')
    .replace(/(.)\/$/, '$1');
  return /^\/[a-z0-9\-/]{0,99}$/.test(limpia) ? limpia : null;
}

/**
 * Pages of tallerza.cl that count by name in `vistas`. Any other valid path
 * (a 404, a made-up POST) counts under CLAVE_OTROS, so "Páginas más vistas"
 * only lists real pages. A new rubro page (src/content/rubros/<slug>.json in
 * apps/landing) must be added here: metricas.test.ts checks it.
 */
export const RUTAS_SITIO = [
  '/',
  '/desabolladura-y-pintura',
  '/demo',
  '/gracias',
  '/privacidad',
  '/terminos',
] as const;
export type RutaSitio = (typeof RUTAS_SITIO)[number];

const esRutaSitio = (r: string): r is RutaSitio => (RUTAS_SITIO as readonly string[]).includes(r);

/**
 * `vistas` key of a beacon path: the site page's key, CLAVE_OTROS for any
 * other well-formed path, or null if the path is malformed (event dropped).
 */
export function claveVista(ruta: string): string | null {
  const limpia = normalizarRuta(ruta);
  if (limpia === null) return null;
  return esRutaSitio(limpia) ? claveMetrica(limpia) : CLAVE_OTROS;
}

const textoOpcional = z.optional(z.string().check(z.maxLength(200)));

/** Server-side check of the beacon body (extra keys are ignored). */
export const esquemaEvento = z.object({
  t: z.enum(TIPOS_EVENTO),
  ruta: z.string().check(z.maxLength(200)),
  ref: textoOpcional,
  utm_source: textoOpcional,
  utm_medium: textoOpcional,
  utm_campaign: textoOpcional,
  movil: z.boolean(),
});

/** 'AAAA-MM-DD' of an instant in Chile (America/Santiago, DST aware). */
export function diaChile(ms: number): string {
  // en-CA formats dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms));
}
